import { supabase } from '../lib/supabase'
import type {
  AutoAssignmentPreview,
  BrokerClassificationKpi,
  BrokerComercialAssignment,
  BrokerComercialGestaoItem,
  BrokerComercialRule,
  BrokerConsultantAssignment,
  BrokerComercialRuleInput,
  BrokerContact,
  ComercialUser,
  ConsultantManualAssignmentPayload,
  ConsultantUser,
  ManualAssignmentPayload,
} from '../types/comercial'

const RULE_VERSION = 'v2_regras_dinamicas'
const SUPABASE_PAGE_SIZE = 1000

/** Corretor ativo na base `brokers` (listagem e gestão comercial usam só este status). */
export const BROKER_STATUS_ATIVO = 'A'

export function isBrokerStatusAtivo(status?: string | null): boolean {
  return normalize(status).toUpperCase() === BROKER_STATUS_ATIVO
}

type BrokerRow = {
  id: string
  name: string
  id_scgp: number | null
  classification: string | null
  classification_previous: string | null
  state: string | null
  city: string | null
  status?: string | null
}

const IN_CHUNK_SIZE = 200

type RuleLike = Pick<
  BrokerComercialRuleInput,
  'categories' | 'states' | 'is_fallback' | 'priority' | 'comercial_user_id' | 'is_active'
>

function normalize(value?: string | null) {
  return (value || '').trim()
}

function isMissingRelationError(error: any): boolean {
  return error?.code === '42P01' || String(error?.message || '').toLowerCase().includes('does not exist')
}

function matchesRule(item: BrokerComercialGestaoItem, rule: RuleLike): boolean {
  if (!rule.is_active) return false
  const category = normalize(item.classification).toUpperCase()
  const uf = normalize(item.state).toUpperCase()
  const ruleCategories = (rule.categories || []).map((c) => normalize(c).toUpperCase()).filter(Boolean)
  const ruleStates = (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean)

  if (ruleCategories.length === 0) return false
  if (!ruleCategories.includes(category)) return false
  if (rule.is_fallback) return true
  if (ruleStates.length === 0) return true
  return ruleStates.includes(uf)
}

function getMatchedRule(item: BrokerComercialGestaoItem, rules: RuleLike[]): RuleLike | null {
  for (const rule of rules) {
    if (matchesRule(item, rule)) return rule
  }
  return null
}

function isEligibleForAutoRule(item: BrokerComercialGestaoItem, rules: RuleLike[]): boolean {
  return getMatchedRule(item, rules) !== null
}

function isManualAssignment(item: BrokerComercialGestaoItem, manualBrokerIds: Set<string>): boolean {
  return manualBrokerIds.has(item.id) || item.assignment?.assignment_type === 'manual'
}

async function loadManualBrokerIdsFromDb(): Promise<Set<string>> {
  const rows = await fetchAllPages<{ broker_id: string }>((from, to) =>
    supabase
      .from('broker_comercial_assignments')
      .select('broker_id')
      .eq('assignment_type', 'manual')
      .range(from, to)
  )
  return new Set(rows.map((r) => r.broker_id))
}

function isMissingRpcError(error: unknown): boolean {
  const err = error as { code?: string; message?: string }
  return (
    err?.code === '42883' ||
    err?.code === 'PGRST202' ||
    String(err?.message || '')
      .toLowerCase()
      .includes('could not find the function')
  )
}

async function fetchAllPages<T>(
  buildQuery: (from: number, to: number) => any
): Promise<T[]> {
  const all: T[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    const to = from + SUPABASE_PAGE_SIZE - 1
    const { data, error } = await buildQuery(from, to)
    if (error) throw error

    const page = (data || []) as T[]
    all.push(...page)

    if (page.length < SUPABASE_PAGE_SIZE) {
      hasMore = false
    } else {
      from += SUPABASE_PAGE_SIZE
    }
  }

  return all
}

function sortBrokerContacts(contacts: BrokerContact[]): BrokerContact[] {
  return [...contacts].sort((a, b) => {
    const partnerA = a.is_partner ? 1 : 0
    const partnerB = b.is_partner ? 1 : 0
    if (partnerB !== partnerA) return partnerB - partnerA
    const nameA = (a.name || '').toLocaleLowerCase('pt-BR')
    const nameB = (b.name || '').toLocaleLowerCase('pt-BR')
    return nameA.localeCompare(nameB, 'pt-BR')
  })
}

async function listActiveUsersByDepartmentNames(
  departmentNames: string[]
): Promise<Array<{ id: string; name: string; is_active: boolean }>> {
  const names = Array.from(
    new Set(departmentNames.map((name) => normalize(name).toLowerCase()).filter(Boolean))
  )
  if (names.length === 0) return []

  const { data: deptRows, error: deptError } = await supabase
    .from('departments')
    .select('id, name')
    .in('name', names)

  if (deptError) throw deptError

  const departmentIds = ((deptRows || []) as Array<{ id: string; name: string | null }>)
    .map((row) => row.id)
    .filter(Boolean)

  if (departmentIds.length === 0) return []

  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, is_active')
    .eq('is_active', true)
    .in('department_id', departmentIds)
    .order('name')

  if (error) throw error

  return ((data || []) as Array<{ id: string; name: string | null; is_active: boolean }>)
    .filter((u) => !!normalize(u.name))
    .map((u) => ({
      id: u.id,
      name: normalize(u.name),
      is_active: !!u.is_active,
    }))
}

export const comercialGestaoService = {
  async listAvailableCategories(): Promise<string[]> {
    const { data, error } = await supabase
      .from('dashcorretor_classifications')
      .select('name')
      .order('name', { ascending: true })

    if (error) throw error

    return Array.from(
      new Set(
        ((data || []) as Array<{ name: string | null }>)
          .map((row) => normalize(row.name).toUpperCase())
          .filter(Boolean)
      )
    )
  },

  /**
   * Contagem de corretores ativos por classificação (view `comercial_gestao_classification_kpis`).
   * SQL: frontend/sqls/comercial_10_gestao_classification_kpis_single_pass.sql
   */
  async getBrokerCountsByClassificationKpis(): Promise<BrokerClassificationKpi[]> {
    const { data, error } = await supabase
      .from('comercial_gestao_classification_kpis')
      .select('name, min_value, color, count, with_production_count')
      .order('min_value', { ascending: true })

    if (error) throw error

    return ((data || []) as Array<{
      name: string
      min_value: number | string
      color: string | null
      count: number | string
      with_production_count: number | string | null
    }>).map((row) => {
      const minVal = typeof row.min_value === 'number' ? row.min_value : Number(row.min_value)
      const countVal = typeof row.count === 'number' ? row.count : Number(row.count)
      const withProductionRaw = row.with_production_count
      const withProductionVal =
        withProductionRaw == null
          ? null
          : typeof withProductionRaw === 'number'
            ? withProductionRaw
            : Number(withProductionRaw)
      return {
        name: normalize(row.name) || row.name,
        min_value: Number.isFinite(minVal) ? minVal : 0,
        color: row.color,
        count: Number.isFinite(countVal) ? countVal : 0,
        with_production_count:
          withProductionVal != null && Number.isFinite(withProductionVal) ? withProductionVal : null,
      }
    })
  },

  /**
   * Usuários elegíveis como "comercial" na gestão: apenas perfis ativos vinculados ao
   * departamento institucional cujo nome interno (`departments.name`) é `comercial`.
   * Cadastro do departamento: Configurações → Usuários → aba Departamentos.
   */
  async listComercialUsers(): Promise<ComercialUser[]> {
    return listActiveUsersByDepartmentNames(['comercial'])
  },

  /**
   * Usuários elegíveis como "consultor" na gestão manual.
   * Usa o mesmo departamento institucional `comercial` da atribuição de comercial.
   */
  async listConsultantUsers(): Promise<ConsultantUser[]> {
    return listActiveUsersByDepartmentNames(['comercial'])
  },

  async listBrokersWithAssignments(): Promise<BrokerComercialGestaoItem[]> {
    const brokerRowsRaw = await fetchAllPages<BrokerRow>((from, to) =>
      supabase
        .from('brokers')
        .select('id, name, id_scgp, classification, classification_previous, state, city, status')
        .eq('status', BROKER_STATUS_ATIVO)
        .order('name')
        .range(from, to)
    )
    const brokerRows = brokerRowsRaw.filter((b) => isBrokerStatusAtivo(b.status))

    const assignments = await fetchAllPages<BrokerComercialAssignment>((from, to) =>
      supabase
        .from('broker_comercial_assignments')
        .select('id, broker_id, comercial_user_id, assignment_type, rule_version, created_at, updated_at, updated_by')
        .range(from, to)
    )
    const assignmentsByBrokerId = new Map(assignments.map((a) => [a.broker_id, a]))

    let consultantAssignments: BrokerConsultantAssignment[] = []
    try {
      consultantAssignments = await fetchAllPages<BrokerConsultantAssignment>((from, to) =>
        supabase
          .from('broker_consultant_assignments')
          .select('id, broker_id, consultant_user_id, assignment_type, rule_version, created_at, updated_at, updated_by')
          .range(from, to)
      )
    } catch (error) {
      if (!isMissingRelationError(error)) throw error
    }
    const consultantAssignmentsByBrokerId = new Map(
      consultantAssignments.map((a) => [a.broker_id, a])
    )

    const comercialUserIds = Array.from(
      new Set(
        Array.from(assignmentsByBrokerId.values())
          .map((a) => a.comercial_user_id)
          .filter(Boolean)
      )
    )

    const comercialNamesById = new Map<string, string>()
    if (comercialUserIds.length > 0) {
      for (let i = 0; i < comercialUserIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = comercialUserIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', chunkIds)

        if (usersError) throw usersError

        ;((users || []) as Array<{ id: string; name: string | null }>).forEach((u) => {
          comercialNamesById.set(u.id, normalize(u.name))
        })
      }
    }

    const consultantUserIds = Array.from(
      new Set(
        Array.from(consultantAssignmentsByBrokerId.values())
          .map((a) => a.consultant_user_id)
          .filter(Boolean)
      )
    )

    const consultantNamesById = new Map<string, string>()
    if (consultantUserIds.length > 0) {
      for (let i = 0; i < consultantUserIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = consultantUserIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', chunkIds)

        if (usersError) throw usersError

        ;((users || []) as Array<{ id: string; name: string | null }>).forEach((u) => {
          consultantNamesById.set(u.id, normalize(u.name))
        })
      }
    }

    const brokerIds = brokerRows.map((b) => b.id)
    const hasProductionByBrokerId = new Map<string, boolean>()
    const premiumByBrokerId = new Map<string, number>()
    for (let i = 0; i < brokerIds.length; i += IN_CHUNK_SIZE) {
      const chunkIds = brokerIds.slice(i, i + IN_CHUNK_SIZE)
      const { data: valueRows, error: valuesError } = await supabase
        .from('dashcorretor_values')
        .select('id_broker, monthly_paid_premium')
        .in('id_broker', chunkIds)

      if (valuesError) throw valuesError

      ;((valueRows || []) as Array<{ id_broker: string; monthly_paid_premium: number | null }>).forEach(
        (row) => {
          const premium = row.monthly_paid_premium ?? 0
          premiumByBrokerId.set(row.id_broker, premium)
          hasProductionByBrokerId.set(row.id_broker, premium > 0)
        }
      )
    }

    return brokerRows.map((b) => {
      const assignment = assignmentsByBrokerId.get(b.id) || null
      const consultantAssignment = consultantAssignmentsByBrokerId.get(b.id) || null
      return {
        id: b.id,
        name: b.name,
        id_scgp: b.id_scgp,
        classification: b.classification,
        classification_previous: b.classification_previous ?? null,
        state: b.state,
        city: b.city,
        assignment,
        comercial_name: assignment ? comercialNamesById.get(assignment.comercial_user_id) || null : null,
        consultant_assignment: consultantAssignment,
        consultant_name: consultantAssignment
          ? consultantNamesById.get(consultantAssignment.consultant_user_id) || null
          : null,
        has_production: hasProductionByBrokerId.get(b.id) ?? false,
        monthly_paid_premium: premiumByBrokerId.get(b.id) ?? 0,
      }
    })
  },

  async listRules(): Promise<BrokerComercialRule[]> {
    const { data, error } = await supabase
      .from('broker_comercial_rules')
      .select('id, name, categories, states, is_fallback, priority, comercial_user_id, is_active, created_at, updated_at')
      .order('priority', { ascending: true })
      .order('created_at', { ascending: true })

    if (error) {
      if (isMissingRelationError(error)) return []
      throw error
    }
    return (data || []) as BrokerComercialRule[]
  },

  async saveRules(rules: BrokerComercialRuleInput[]): Promise<void> {
    const sanitized = rules.map((rule) => ({
      id: rule.id,
      name: normalize(rule.name) || 'Regra sem nome',
      categories: (rule.categories || []).map((c) => normalize(c)).filter(Boolean),
      states: (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean),
      is_fallback: !!rule.is_fallback,
      priority: Number.isFinite(rule.priority) ? Number(rule.priority) : 100,
      comercial_user_id: rule.comercial_user_id,
      is_active: !!rule.is_active,
    }))

    const { error: deleteError } = await supabase
      .from('broker_comercial_rules')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000')
    if (deleteError) {
      if (isMissingRelationError(deleteError)) {
        throw new Error('Tabela de regras não encontrada. Execute o SQL comercial_05_gestao_regras.sql.')
      }
      throw deleteError
    }

    if (sanitized.length === 0) return

    const { error: insertError } = await supabase
      .from('broker_comercial_rules')
      .insert(sanitized)
    if (insertError) throw insertError
  },

  async createRule(rule: BrokerComercialRuleInput): Promise<void> {
    const payload = {
      name: normalize(rule.name) || 'Regra sem nome',
      categories: (rule.categories || []).map((c) => normalize(c).toUpperCase()).filter(Boolean),
      states: (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean),
      is_fallback: !!rule.is_fallback,
      priority: Number.isFinite(rule.priority) ? Number(rule.priority) : 100,
      comercial_user_id: rule.comercial_user_id,
      is_active: !!rule.is_active,
    }

    const { error } = await supabase
      .from('broker_comercial_rules')
      .insert(payload)

    if (error) throw error
  },

  async updateRule(rule: BrokerComercialRuleInput): Promise<void> {
    if (!rule.id) throw new Error('ID da regra é obrigatório para edição.')

    const payload = {
      name: normalize(rule.name) || 'Regra sem nome',
      categories: (rule.categories || []).map((c) => normalize(c).toUpperCase()).filter(Boolean),
      states: (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean),
      is_fallback: !!rule.is_fallback,
      priority: Number.isFinite(rule.priority) ? Number(rule.priority) : 100,
      comercial_user_id: rule.comercial_user_id,
      is_active: !!rule.is_active,
    }

    const { error } = await supabase.from('broker_comercial_rules').update(payload).eq('id', rule.id)

    if (error) throw error
  },

  async deleteRule(ruleId: string): Promise<void> {
    const { error } = await supabase
      .from('broker_comercial_rules')
      .delete()
      .eq('id', ruleId)

    if (error) throw error
  },

  async fetchManualBrokerIds(): Promise<Set<string>> {
    return loadManualBrokerIdsFromDb()
  },

  buildAutoAssignmentPreview(
    items: BrokerComercialGestaoItem[],
    rules: RuleLike[],
    comercialUsers: ComercialUser[],
    manualBrokerIds: Set<string> = new Set()
  ): AutoAssignmentPreview {
    const preview: AutoAssignmentPreview = {
      totalEligible: 0,
      totalPendingManual: 0,
      totalIgnoredManual: 0,
      totalNoRuleMatch: 0,
      totalToClear: 0,
      byComercial: [],
    }

    const byComercialMap = new Map<string, number>()
    const userNameById = new Map(comercialUsers.map((u) => [u.id, u.name]))
    const sortedRules = [...rules].filter((r) => r.is_active).sort((a, b) => a.priority - b.priority)

    items.forEach((item) => {
      if (item.assignment?.assignment_type === 'auto' && !isEligibleForAutoRule(item, sortedRules)) {
        preview.totalToClear += 1
      }

      const hasManualAssignment = isManualAssignment(item, manualBrokerIds)

      // Ignorados por já estarem manuais (dentro do universo automático).
      if (hasManualAssignment) {
        preview.totalIgnoredManual += 1
        return
      }

      const matchedRule = getMatchedRule(item, sortedRules)
      if (!matchedRule) {
        preview.totalNoRuleMatch += 1
        return
      }

      preview.totalEligible += 1
      const comercialName = userNameById.get(matchedRule.comercial_user_id) || 'Comercial sem nome'
      byComercialMap.set(comercialName, (byComercialMap.get(comercialName) || 0) + 1)
    })

    preview.byComercial = Array.from(byComercialMap.entries()).map(([comercialName, count]) => ({
      comercialName,
      count,
    }))

    return preview
  },

  async applyAutoAssignments(
    items: BrokerComercialGestaoItem[],
    rules: RuleLike[]
  ): Promise<{ updated: number; cleared: number; skipped: number }> {
    const { data: authData } = await supabase.auth.getUser()
    const currentUserId = authData.user?.id || null
    const sortedRules = [...rules].filter((r) => r.is_active).sort((a, b) => a.priority - b.priority)
    const manualBrokerIds = await loadManualBrokerIdsFromDb()

    const upserts: Array<{
      broker_id: string
      comercial_user_id: string
      assignment_type: 'auto'
      rule_version: string
      updated_by: string | null
    }> = []

    const brokerIdsToClear: string[] = []
    let skipped = 0

    for (const item of items) {
      if (isManualAssignment(item, manualBrokerIds)) {
        skipped += 1
        continue
      }

      if (isEligibleForAutoRule(item, sortedRules)) {
        const matchedRule = getMatchedRule(item, sortedRules)!
        upserts.push({
          broker_id: item.id,
          comercial_user_id: matchedRule.comercial_user_id,
          assignment_type: 'auto',
          rule_version: RULE_VERSION,
          updated_by: currentUserId,
        })
        continue
      }

      if (item.assignment?.assignment_type === 'auto') {
        brokerIdsToClear.push(item.id)
        continue
      }

      skipped += 1
    }

    const safeUpserts = upserts.filter((row) => !manualBrokerIds.has(row.broker_id))
    const rpcPayload = safeUpserts.map((row) => ({
      broker_id: row.broker_id,
      comercial_user_id: row.comercial_user_id,
      rule_version: row.rule_version,
      updated_by: row.updated_by,
    }))

    let updated = 0
    const { data: rpcResult, error: rpcError } = await supabase.rpc('rpc_comercial_apply_auto_assignments', {
      p_upserts: rpcPayload,
      p_broker_ids_to_clear: brokerIdsToClear,
    })

    if (!rpcError) {
      const result = rpcResult as { updated?: number; cleared?: number } | null
      updated = typeof result?.updated === 'number' ? result.updated : safeUpserts.length
    } else if (isMissingRpcError(rpcError)) {
      const chunkSize = 500
      for (let i = 0; i < safeUpserts.length; i += chunkSize) {
        const chunk = safeUpserts.slice(i, i + chunkSize)
        const { error } = await supabase
          .from('broker_comercial_assignments')
          .upsert(chunk, { onConflict: 'broker_id' })

        if (error) throw error
      }

      for (let i = 0; i < brokerIdsToClear.length; i += IN_CHUNK_SIZE) {
        const chunk = brokerIdsToClear.slice(i, i + IN_CHUNK_SIZE)
        const { error } = await supabase
          .from('broker_comercial_assignments')
          .delete()
          .in('broker_id', chunk)
          .eq('assignment_type', 'auto')

        if (error) throw error
      }
      updated = safeUpserts.length
    } else {
      throw rpcError
    }

    const cleared =
      !rpcError && rpcResult && typeof (rpcResult as { cleared?: number }).cleared === 'number'
        ? (rpcResult as { cleared: number }).cleared
        : brokerIdsToClear.length

    return { updated, cleared, skipped }
  },

  async assignManual(payload: ManualAssignmentPayload): Promise<void> {
    const brokerIds = Array.from(new Set(payload.brokerIds.filter(Boolean)))
    if (brokerIds.length === 0) return

    const { data: brokerStatusRows, error: statusError } = await supabase
      .from('brokers')
      .select('id, status')
      .in('id', brokerIds)

    if (statusError) throw statusError

    const activeBrokerIds = ((brokerStatusRows || []) as Array<{ id: string; status?: string | null }>)
      .filter((b) => isBrokerStatusAtivo(b.status))
      .map((b) => b.id)

    if (activeBrokerIds.length === 0) {
      throw new Error('Nenhum corretor ativo selecionado para atribuição.')
    }

    const { data: authData } = await supabase.auth.getUser()
    const currentUserId = authData.user?.id || null
    const rows = activeBrokerIds.map((brokerId) => ({
      broker_id: brokerId,
      comercial_user_id: payload.comercialUserId,
      assignment_type: 'manual' as const,
      rule_version: null,
      updated_by: currentUserId,
    }))

    const { error } = await supabase
      .from('broker_comercial_assignments')
      .upsert(rows, { onConflict: 'broker_id' })

    if (error) throw error
  },

  async assignConsultantManual(payload: ConsultantManualAssignmentPayload): Promise<void> {
    const brokerIds = Array.from(new Set(payload.brokerIds.filter(Boolean)))
    if (brokerIds.length === 0) return

    const { data: brokerStatusRows, error: statusError } = await supabase
      .from('brokers')
      .select('id, status')
      .in('id', brokerIds)

    if (statusError) throw statusError

    const activeBrokerIds = ((brokerStatusRows || []) as Array<{ id: string; status?: string | null }>)
      .filter((b) => isBrokerStatusAtivo(b.status))
      .map((b) => b.id)

    if (activeBrokerIds.length === 0) {
      throw new Error('Nenhum corretor ativo selecionado para atribuição.')
    }

    const { data: authData } = await supabase.auth.getUser()
    const currentUserId = authData.user?.id || null
    const rows = activeBrokerIds.map((brokerId) => ({
      broker_id: brokerId,
      consultant_user_id: payload.consultantUserId,
      assignment_type: 'manual' as const,
      rule_version: null,
      updated_by: currentUserId,
    }))

    const { error } = await supabase
      .from('broker_consultant_assignments')
      .upsert(rows, { onConflict: 'broker_id' })

    if (error) throw error
  },

  /** Remove a linha de vínculo (corretor fica sem responsável comercial atribuído). */
  async removeBrokerComercialAssignment(brokerId: string): Promise<void> {
    if (!brokerId) return
    const { error } = await supabase.from('broker_comercial_assignments').delete().eq('broker_id', brokerId)
    if (error) throw error
  },

  /** Remove a linha de vínculo do consultor (corretor fica sem consultor atribuído). */
  async removeBrokerConsultantAssignment(brokerId: string): Promise<void> {
    if (!brokerId) return
    const { error } = await supabase
      .from('broker_consultant_assignments')
      .delete()
      .eq('broker_id', brokerId)
    if (error) throw error
  },

  /**
   * Mapa broker_id → { comercialUserId, name } (vínculos em broker_comercial_assignments).
   */
  async getBrokerComercialAssignmentMap(): Promise<
    Map<string, { comercialUserId: string; name: string }>
  > {
    const assignments = await fetchAllPages<{ broker_id: string; comercial_user_id: string }>((from, to) =>
      supabase
        .from('broker_comercial_assignments')
        .select('broker_id, comercial_user_id')
        .range(from, to)
    )

    const comercialUserIds = Array.from(
      new Set(assignments.map((a) => a.comercial_user_id).filter(Boolean))
    )

    const comercialNamesById = new Map<string, string>()
    if (comercialUserIds.length > 0) {
      for (let i = 0; i < comercialUserIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = comercialUserIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', chunkIds)

        if (usersError) {
          if (isMissingRelationError(usersError)) break
          throw usersError
        }

        ;((users || []) as Array<{ id: string; name: string | null }>).forEach((u) => {
          const name = normalize(u.name)
          if (name) comercialNamesById.set(u.id, name)
        })
      }
    }

    const map = new Map<string, { comercialUserId: string; name: string }>()
    assignments.forEach((a) => {
      if (!a.comercial_user_id) return
      const name = comercialNamesById.get(a.comercial_user_id)
      if (name) {
        map.set(a.broker_id, { comercialUserId: a.comercial_user_id, name })
      }
    })
    return map
  },

  /**
   * Mapa broker_id → comercial apenas para os IDs informados.
   */
  async getBrokerComercialAssignmentMapForBrokerIds(
    brokerIds: string[]
  ): Promise<Map<string, { comercialUserId: string; name: string }>> {
    const uniqueIds = Array.from(new Set(brokerIds.filter(Boolean)))
    if (uniqueIds.length === 0) {
      return new Map()
    }

    const assignments: { broker_id: string; comercial_user_id: string }[] = []
    for (let i = 0; i < uniqueIds.length; i += IN_CHUNK_SIZE) {
      const chunkIds = uniqueIds.slice(i, i + IN_CHUNK_SIZE)
      const { data, error } = await supabase
        .from('broker_comercial_assignments')
        .select('broker_id, comercial_user_id')
        .in('broker_id', chunkIds)

      if (error) {
        if (isMissingRelationError(error)) return new Map()
        throw error
      }
      assignments.push(...((data ?? []) as { broker_id: string; comercial_user_id: string }[]))
    }

    const comercialUserIds = Array.from(
      new Set(assignments.map((a) => a.comercial_user_id).filter(Boolean))
    )

    const comercialNamesById = new Map<string, string>()
    if (comercialUserIds.length > 0) {
      for (let i = 0; i < comercialUserIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = comercialUserIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', chunkIds)

        if (usersError) {
          if (isMissingRelationError(usersError)) break
          throw usersError
        }

        ;((users || []) as Array<{ id: string; name: string | null }>).forEach((u) => {
          const name = normalize(u.name)
          if (name) comercialNamesById.set(u.id, name)
        })
      }
    }

    const map = new Map<string, { comercialUserId: string; name: string }>()
    assignments.forEach((a) => {
      if (!a.comercial_user_id) return
      const name = comercialNamesById.get(a.comercial_user_id)
      if (name) {
        map.set(a.broker_id, { comercialUserId: a.comercial_user_id, name })
      }
    })
    return map
  },

  /** Mapa broker_id → nome do comercial responsável (vínculos em broker_comercial_assignments). */
  async getBrokerComercialNameMap(): Promise<Map<string, string>> {
    const assignmentMap = await this.getBrokerComercialAssignmentMap()
    const map = new Map<string, string>()
    assignmentMap.forEach((value, brokerId) => {
      map.set(brokerId, value.name)
    })
    return map
  },

  async getComercialNameByBrokerId(brokerId: string): Promise<string | null> {
    if (!brokerId) return null

    const { data: assignment, error: assignmentError } = await supabase
      .from('broker_comercial_assignments')
      .select('comercial_user_id')
      .eq('broker_id', brokerId)
      .maybeSingle()

    if (assignmentError) {
      if (isMissingRelationError(assignmentError)) return null
      throw assignmentError
    }

    const comercialUserId = assignment?.comercial_user_id
    if (!comercialUserId) return null

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('name')
      .eq('id', comercialUserId)
      .maybeSingle()

    if (profileError) throw profileError
    return normalize(profile?.name ?? null) || null
  },

  /**
   * Mapa broker_id → { consultantUserId, name } (vínculos em broker_consultant_assignments).
   */
  async getBrokerConsultantAssignmentMap(): Promise<
    Map<string, { consultantUserId: string; name: string }>
  > {
    let assignments: { broker_id: string; consultant_user_id: string }[] = []
    try {
      assignments = await fetchAllPages<{ broker_id: string; consultant_user_id: string }>((from, to) =>
        supabase
          .from('broker_consultant_assignments')
          .select('broker_id, consultant_user_id')
          .range(from, to)
      )
    } catch (error) {
      if (isMissingRelationError(error)) return new Map()
      throw error
    }

    const consultantUserIds = Array.from(
      new Set(assignments.map((a) => a.consultant_user_id).filter(Boolean))
    )

    const consultantNamesById = new Map<string, string>()
    if (consultantUserIds.length > 0) {
      for (let i = 0; i < consultantUserIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = consultantUserIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', chunkIds)

        if (usersError) {
          if (isMissingRelationError(usersError)) break
          throw usersError
        }

        ;((users || []) as Array<{ id: string; name: string | null }>).forEach((u) => {
          const name = normalize(u.name)
          if (name) consultantNamesById.set(u.id, name)
        })
      }
    }

    const map = new Map<string, { consultantUserId: string; name: string }>()
    assignments.forEach((a) => {
      if (!a.consultant_user_id) return
      const name = consultantNamesById.get(a.consultant_user_id)
      if (name) {
        map.set(a.broker_id, { consultantUserId: a.consultant_user_id, name })
      }
    })
    return map
  },

  /**
   * Mapa broker_id → consultor apenas para os IDs informados.
   */
  async getBrokerConsultantAssignmentMapForBrokerIds(
    brokerIds: string[]
  ): Promise<Map<string, { consultantUserId: string; name: string }>> {
    const uniqueIds = Array.from(new Set(brokerIds.filter(Boolean)))
    if (uniqueIds.length === 0) {
      return new Map()
    }

    const assignments: { broker_id: string; consultant_user_id: string }[] = []
    for (let i = 0; i < uniqueIds.length; i += IN_CHUNK_SIZE) {
      const chunkIds = uniqueIds.slice(i, i + IN_CHUNK_SIZE)
      const { data, error } = await supabase
        .from('broker_consultant_assignments')
        .select('broker_id, consultant_user_id')
        .in('broker_id', chunkIds)

      if (error) {
        if (isMissingRelationError(error)) return new Map()
        throw error
      }
      assignments.push(...((data ?? []) as { broker_id: string; consultant_user_id: string }[]))
    }

    const consultantUserIds = Array.from(
      new Set(assignments.map((a) => a.consultant_user_id).filter(Boolean))
    )

    const consultantNamesById = new Map<string, string>()
    if (consultantUserIds.length > 0) {
      for (let i = 0; i < consultantUserIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = consultantUserIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', chunkIds)

        if (usersError) {
          if (isMissingRelationError(usersError)) break
          throw usersError
        }

        ;((users || []) as Array<{ id: string; name: string | null }>).forEach((u) => {
          const name = normalize(u.name)
          if (name) consultantNamesById.set(u.id, name)
        })
      }
    }

    const map = new Map<string, { consultantUserId: string; name: string }>()
    assignments.forEach((a) => {
      if (!a.consultant_user_id) return
      const name = consultantNamesById.get(a.consultant_user_id)
      if (name) {
        map.set(a.broker_id, { consultantUserId: a.consultant_user_id, name })
      }
    })
    return map
  },

  /** Mapa broker_id → nome do consultor responsável (vínculos em broker_consultant_assignments). */
  async getBrokerConsultantNameMap(): Promise<Map<string, string>> {
    const assignmentMap = await this.getBrokerConsultantAssignmentMap()
    const map = new Map<string, string>()
    assignmentMap.forEach((value, brokerId) => {
      map.set(brokerId, value.name)
    })
    return map
  },

  async getConsultantNameByBrokerId(brokerId: string): Promise<string | null> {
    if (!brokerId) return null

    const { data: assignment, error: assignmentError } = await supabase
      .from('broker_consultant_assignments')
      .select('consultant_user_id')
      .eq('broker_id', brokerId)
      .maybeSingle()

    if (assignmentError) {
      if (isMissingRelationError(assignmentError)) return null
      throw assignmentError
    }

    const consultantUserId = assignment?.consultant_user_id
    if (!consultantUserId) return null

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('name')
      .eq('id', consultantUserId)
      .maybeSingle()

    if (profileError) throw profileError
    return normalize(profile?.name ?? null) || null
  },

  async listBrokerContacts(brokerId: string): Promise<BrokerContact[]> {
    if (!brokerId) return []
    const { data, error } = await supabase
      .from('broker_contacts')
      .select('id, broker_id, name, is_partner, department, role, phone, email, created_at')
      .eq('broker_id', brokerId)
      .order('name', { ascending: true, nullsFirst: false })

    if (error) throw error

    return sortBrokerContacts((data || []) as BrokerContact[])
  },

  async listBrokerContactsByBrokerIds(brokerIds: string[]): Promise<BrokerContact[]> {
    const ids = Array.from(new Set(brokerIds.filter(Boolean)))
    if (ids.length === 0) return []

    const all: BrokerContact[] = []
    for (let i = 0; i < ids.length; i += IN_CHUNK_SIZE) {
      const chunkIds = ids.slice(i, i + IN_CHUNK_SIZE)
      const page = await fetchAllPages<BrokerContact>((from, to) =>
        supabase
          .from('broker_contacts')
          .select('id, broker_id, name, is_partner, department, role, phone, email, created_at')
          .in('broker_id', chunkIds)
          .order('name', { ascending: true, nullsFirst: false })
          .range(from, to)
      )
      all.push(...page)
    }

    return sortBrokerContacts(all)
  },
}
